
import { Component, Prop, Vue } from 'vue-property-decorator'
import { IPlatform, IAcct } from '@/types/media-types'
import { MediaService } from '@/services/index'

// 模块级 Promise 缓存：所有 PlatformCrawlStatus 实例共享，避免重复请求
let mpTypeGroupPromise: Promise<any[]> | null = null

enum status {
    CONFIGURED_SUCCESS = 'CONFIGURED_SUCCESS',
    CONFIGURED_PARTIAL = 'CONFIGURED_PARTIAL',
    CONFIGURED_FAILED = 'CONFIGURED_FAILED',
    CONFIGURED_STOP_UPDATE = 'CONFIGURED_STOP_UPDATE',
    CHECKED_NOT_CONFIGURED = 'CHECKED_NOT_CONFIGURED',
    CHECKED_NOT_EXIST = 'CHECKED_NOT_EXIST',
    NOT_CHECKED = 'NOT_CHECKED'
}

const mapStatusToTxt = {
    CONFIGURED_SUCCESS: '√',
    CONFIGURED_PARTIAL: '▓',
    CONFIGURED_FAILED: '╳',
    CONFIGURED_STOP_UPDATE: '！',
    CHECKED_NOT_CONFIGURED: '☉',
    CHECKED_NOT_EXIST: '□',
    NOT_CHECKED: '?'
}

@Component({
    name: 'PlatformCrawlStatus',
    components: {
    }
})
export default class extends Vue {
    @Prop({ required: true }) private mediumId!: number 
    @Prop({ required: true }) private platform!: IPlatform
    @Prop({ required: false }) private updatePlatformCallback!: Function
    private platformBindingStatus: string = this.platform.binding_status || status.NOT_CHECKED
    private platformAccountMemo: string = ''

    private currentAccount: IAcct | undefined = undefined 
    private accountsDialogVisible: boolean = false
    private accountMemoDialogVisible: boolean = false

    // AI 检测相关
    private aiCheckLoading: boolean = false
    private aiHistoryList: any[] = []
    private aiHistoryLoading: boolean = false
    private currentSourceGroupId: string = ''

    private memoOptions = [
        { value: status.CONFIGURED_STOP_UPDATE, label: '！（已配置，账号已停更）' },
        { value: status.CONFIGURED_FAILED, label: '╳（已配置，爬取失败）' }
    ]
    private bindingOptions = [
        { value: status.CHECKED_NOT_CONFIGURED, label: '☉（已检查有，未配置）' },
        { value: status.CHECKED_NOT_EXIST, label: '□（已检查，不存在）' },
        { value: status.NOT_CHECKED, label: '?（未检查，不知）' }
    ]

    private mapAccountsStatus(platform: IPlatform) {
        const accounts = platform.accounts || []

        if (accounts.length === 0) {
            if (platform.binding_status) {
                return (mapStatusToTxt as any)[platform.binding_status] 
            }
            return mapStatusToTxt.NOT_CHECKED
        }

        const isAllFailed = accounts.every(account => {
            if (account.memo) {
                return account.memo === status.CONFIGURED_FAILED
            }
            return account.collection_status === '异常'
        })
        const isAllSuccess = accounts.every(account => account.collection_status === '正常')
        const isAllStopUpdate = accounts.every(account => account.memo === status.CONFIGURED_STOP_UPDATE)
        const isPartialSuccess = !isAllSuccess && !isAllFailed

        if (isAllSuccess) {
            return mapStatusToTxt.CONFIGURED_SUCCESS
        }
        if (isAllFailed) {
            return mapStatusToTxt.CONFIGURED_FAILED
        }
        if (isAllStopUpdate) {
            return mapStatusToTxt.CONFIGURED_STOP_UPDATE
        }
        if (isPartialSuccess) {
            return mapStatusToTxt.CONFIGURED_PARTIAL
        }

        return mapStatusToTxt.NOT_CHECKED
    }

    private mapAccountStatus(account: IAcct) {  
        const { collection_status, memo } = account

        if (memo) {
            return (mapStatusToTxt as any)[memo]
        }

        switch (collection_status) {
            case '正常':
                return mapStatusToTxt.CONFIGURED_SUCCESS
            case '异常':
                return mapStatusToTxt.CONFIGURED_FAILED
            default:
                return mapStatusToTxt.CONFIGURED_SUCCESS
        }   
    }

    private handleAccountsDialogOpen() {
        this.accountsDialogVisible = true
        this.loadAiHistory()
    }

    private handleAccountsDialogClose() {
        this.accountsDialogVisible = false
    }

    private handleAccountMemoDialogOpen(account: IAcct) {
        if (account.collection_status === '正常') return

        this.currentAccount = account
        this.platformAccountMemo = account.memo ? account.memo : (account.collection_status === '正常' ? status.CONFIGURED_SUCCESS : status.CONFIGURED_FAILED)
        this.accountMemoDialogVisible = true 
    }

    private handleAccountMemoDialogClose() {
        this.accountMemoDialogVisible = false
    }

    private handlePlatformBindingStatusChange(value: string) {
        this.platformBindingStatus = value
    }

    private async handleAccountMemoUpdate() {
        const params = {
            mp_id: (this.currentAccount as IAcct).id,
            data: this.platformAccountMemo
        }
        const result = await MediaService.updateMpAccountMemo(params)
        if (result.status === 200) {
            this.$message.success('更新成功')
            this.accountMemoDialogVisible = false
            const account = this.platform.accounts.find(item => item.id === (this.currentAccount as IAcct).id)
            if (account) {
                account.memo = this.platformAccountMemo
            }
        } else {
            this.$message.error('更新失败')
        }
    }

    private async handlePlatformBindingStatus() {
        const params = {
            medium_id: this.mediumId,
            binding_status_id: this.platform.id,
            binding_status_name: this.platform.name,
            binding_status: this.platformBindingStatus
        }

        const result = await MediaService.updateMediaPlatformBindingStatus(params)
        if (result.status === 200) {
            this.$message.success('更新成功')
            this.accountsDialogVisible = false
            this.updatePlatformCallback && this.updatePlatformCallback(this.mediumId, this.platform.id, this.platformBindingStatus)
        } else {
            this.$message.error('更新失败')
        }
    }

    private async mounted() {
        // 预留，后续扩展
    }

    private ensureMpTypeGroup(): Promise<any[]> {
        if (mpTypeGroupPromise) return mpTypeGroupPromise
        mpTypeGroupPromise = MediaService.getMpTypeGroup()
            .then((res: any) => {
                if (res && res.status === 200 && res.data) return res.data
                return []
            })
            .catch(() => {
                mpTypeGroupPromise = null
                return []
            })
        return mpTypeGroupPromise
    }

    private async fetchSourceGroupId(): Promise<string> {
        const platformName = this.platform && this.platform.name
        if (!platformName) return ''
        const list = await this.ensureMpTypeGroup()
        const matched = list.find((g: any) => {
            return Array.isArray(g.children) && g.children.some((c: any) => c.label === platformName)
        })
        return matched ? String(matched.value) : ''
    }

    private async handleAiCheck() {
        if (!this.platform || !this.platform.id) {
            this.$message.warning('平台信息缺失')
            return
        }
        this.aiCheckLoading = true
        try {
            const sourceGroupId = await this.fetchSourceGroupId()
            this.currentSourceGroupId = sourceGroupId
            const result = await MediaService.triggerAiCheck({
                mediaId: String(this.mediumId),
                sourceGroupId,
                sourceId: String(this.platform.id)
            })
            if (result.status === 200 && result.data && result.data.success) {
                this.$message.success('AI 检测完成')
                // 将 AI 判定结果同步到人工状态下拉框，并立即刷新表格图标
                const aiResult = result.data.mediumExistCheckResult
                const aiExists = aiResult && aiResult.aiExists
                if (aiExists === 1) {
                    this.platformBindingStatus = status.CHECKED_NOT_CONFIGURED
                    this.updatePlatformCallback && this.updatePlatformCallback(this.mediumId, this.platform.id, status.CHECKED_NOT_CONFIGURED)
                } else if (aiExists === 0) {
                    this.platformBindingStatus = status.CHECKED_NOT_EXIST
                    this.updatePlatformCallback && this.updatePlatformCallback(this.mediumId, this.platform.id, status.CHECKED_NOT_EXIST)
                }
            } else {
                this.$message.error(result.message || '检测失败')
            }
        } catch (e: any) {
            this.$message.error((e && e.message) || '接口请求失败')
        } finally {
            this.aiCheckLoading = false
            await this.loadAiHistory()
        }
    }

    private async loadAiHistory() {
        if (!this.platform || !this.platform.id) return
        this.aiHistoryLoading = true
        try {
            // 弹窗打开时可能还没触发过 AI 检测，先确保有 sourceGroupId
            if (!this.currentSourceGroupId) {
                this.currentSourceGroupId = await this.fetchSourceGroupId()
            }
            const result = await MediaService.getAnalysisList({
                mediaId: String(this.mediumId),
                sourceGroupId: this.currentSourceGroupId,
                sourceId: String(this.platform.id)
            })
            if (result.status === 200 && Array.isArray(result.data)) {
                this.aiHistoryList = result.data
            } else {
                this.aiHistoryList = []
            }
        } catch (e) {
            this.aiHistoryList = []
        } finally {
            this.aiHistoryLoading = false
        }
    }

    private async refreshAiHistory() {
        await this.loadAiHistory()
    }

    private getHistoryIcon(item: any): string {
        if (item.aiExists === 1) return '✓'
        if (item.aiExists === 0) return '✗'
        return '?'
    }

    private getHistoryIconClass(item: any): string {
        if (item.aiExists === 1) return 'ai-exists'
        if (item.aiExists === 0) return 'ai-not-exists'
        return 'ai-unknown'
    }

    private getHistoryLabel(item: any): string {
        if (item.aiExists === 1) return '存在'
        if (item.aiExists === 0) return '不存在'
        return '解析失败'
    }

    private getActiveIcon(item: any): string {
        if (item.aiActive === 1) return '✓'
        if (item.aiActive === 0) return '✗'
        return '?'
    }

    private getActiveBadgeClass(item: any): string {
        if (item.aiActive === 1) return 'ai-active'
        if (item.aiActive === 0) return 'ai-not-active'
        return 'ai-unknown'
    }

    private getActiveLabel(item: any): string {
        if (item.aiActive === 1) return '活跃'
        if (item.aiActive === 0) return '不活跃'
        return '未知'
    }

    private formatDateTime(value: any): string {
        if (!value) return ''
        const d = new Date(value)
        if (isNaN(d.getTime())) return String(value)
        const p = (n: number) => (n < 10 ? '0' + n : '' + n)
        return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds())
    }

    private getStatusClass(status: string): string {
        switch (status) {
            case mapStatusToTxt.CONFIGURED_SUCCESS:
                return 'status-success'
            case mapStatusToTxt.CONFIGURED_FAILED:
                return 'status-failed'
            default:
                return ''
        }
    }
}
